import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Row } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useGetBusinessPartnerUsersChartQuery } from '../../../../app/services/chatbot-rtk.services';
import Alert from '../../../../components/common/Alert';
import LoadingComponent from '../../../../components/common/loading/loading.component';
import { AlertsType } from '../../../../constants/alerts.constant';

// Chart.js registration
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const GraphUserCounter: React.FC = () => {
  const { t } = useTranslation();

  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const [dayRange, setDayRange] = useState<number>(30);

  const { isLoading, isError, isSuccess, refetch, data, isFetching } = useGetBusinessPartnerUsersChartQuery(dayRange);

  // Helper function to format a timestamp into a date string (e.g., "YYYY-MM-DD")
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (data) {
      const labels = data.map(dr => formatDate(dr.date_time));

      const chartDataSimple = {
        labels,
        datasets: [
          {
            label: t('BusinessPartner/GraphUserListBusinessPartner/numberRegistration'),
            data: data.map(dr => dr.count),
            borderColor: 'rgb(8, 65, 255)',
            backgroundColor: 'rgb(8, 65, 255)',
          },
        ],
      };

      const options = {
        responsive: true,
        scales: {
          y: {
            ticks: {
              callback: function (value: any) {
                if (typeof value === 'number' && value % 1 === 0) {
                  return value;
                } else {
                  return '';
                }
              },
              stepSize: 10,
            },
          },
        },
      };

      setChartOptions(options);
      setChartData(chartDataSimple);
    }
  }, [isSuccess, isFetching]);

  useEffect(() => {
    refetch();
  }, [dayRange]);

  let content;

  if (isError) {
    content = (
      <Row className=" tw-w-5/6 tw-mx-auto tw-mt-2">
        <Alert type="Error">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="m-0">{t(AlertsType.ISKBKO003)}</p>
            <PSButton id="btn-graph-refetch" size="sm" className="px-3" onClick={() => refetch()} title={t('KnowledgeBasesCreateConditionalScenario/btn-01')} />
          </div>
        </Alert>
      </Row>
    );
  }

  if (isSuccess && chartData) {
    content = (
      <React.Fragment>
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
          <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('BusinessPartner/GraphUserListBusinessPartner/sectionTitle')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
            {[7, 30, 90].map(day => (
              <button
                id={`btn-${day}-days`}
                key={day}
                onClick={() => setDayRange(day)}
                className={`tw-grow tw-shrink tw-basis-0 tw-px-2.5 tw-py-1.5 tw-rounded-[99px] ${
                  dayRange === day ? 'tw-bg-gray-100' : 'tw-bg-white'
                } tw-border tw-border-solid tw-border-gray-200 tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-inline-flex`}
              >
                <span className="tw-self-stretch tw-h-4 tw-text-center tw-text-gray-800 tw-text-xs tw-font-medium tw-font-['Lexend'] tw-leading-none">
                  Last {day} days
                </span>
              </button>
            ))}
          </div>
        </div>

        {chartData && <Line options={chartOptions} data={chartData} />}
      </React.Fragment>
    );
  }

  return (
    <div className="tw-w-full tw-px-6 tw-pt-4 tw-pb-6 lg:tw-max-h-[530px] tw-max-h-auto   tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-inline-flex">
      {content}
      {(isLoading || isFetching) && <LoadingComponent />}
    </div>
  );
};

export default WrappedErrorBoundary(GraphUserCounter);
